import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, ArrowRight, List, Download } from 'lucide-react';
import { api, API, pad } from '../../lib/api';
import PaperBody from '../../components/paper/PaperBody';
import ShareBar from '../../components/u99/ShareBar';

const NavCard = ({ c, bookSlug, dir }) => (
  <Link to={`/u99/book/${bookSlug}/${c.slug}`} className={`group flex-1 rounded-2xl border border-white/[0.08] hover:border-amber-300/40 p-6 transition-colors duration-500 ${dir === 'next' ? 'text-right' : ''}`} data-testid={`chapter-nav-${dir}`}>
    <p className={`font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-2 ${dir === 'next' ? 'justify-end' : ''}`}>
      {dir === 'prev' && <ArrowLeft size={12} />} Chapter {pad(c.number)} {dir === 'next' && <ArrowRight size={12} />}
    </p>
    <p className="font-celestial text-lg text-slate-200 group-hover:text-amber-100 mt-3 transition-colors">{c.title}</p>
  </Link>
);

export default function ChapterPage() {
  const { slug, chapter } = useParams();
  const [ch, setCh] = useState(null);
  const [error, setError] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setCh(null);
    setError(false);
    api.get(`/books/${slug}/chapters/${chapter}`).then((r) => { setCh(r.data); document.title = `${r.data.title} — ${r.data.book.title}`; }).catch(() => setError(true));
  }, [slug, chapter]);

  if (error) {
    return (
      <main className="pt-40 pb-40 text-center px-6" data-testid="chapter-not-found">
        <p className="font-mono-precision text-xs tracking-[0.3em] text-amber-300">PAGE MISSING</p>
        <h1 className="font-celestial text-3xl text-slate-100 mt-4">This chapter is not published</h1>
        <Link to={`/u99/book/${slug}`} className="inline-block mt-8 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300">← Contents</Link>
      </main>
    );
  }
  if (!ch) return <div className="pt-48 pb-48 text-center font-mono-precision text-xs tracking-[0.3em] text-slate-600">TURNING THE PAGE…</div>;

  return (
    <main className="min-h-screen" data-testid="chapter-page">
      <motion.div className="fixed top-16 left-0 right-0 h-px bg-amber-400 origin-left z-40" style={{ scaleX: progress }} data-testid="chapter-progress-bar" />
      <header className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 nebula-mesh overflow-hidden">
        <span className="absolute -top-6 right-0 lg:right-10 font-celestial text-[38vw] lg:text-[22vw] leading-none text-white/[0.025] select-none pointer-events-none" aria-hidden="true">{pad(ch.number)}</span>
        <div className="relative max-w-4xl mx-auto px-5 lg:px-10">
          <Link to={`/u99/book/${slug}`} className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-amber-300 transition-colors" data-testid="chapter-contents-link"><List size={12} /> {ch.book.title} · Contents</Link>
          <p className="mt-10 font-mono-precision text-xs uppercase tracking-[0.3em] text-amber-300">{ch.book.title} · {ch.part_title ? `${ch.part_title} · ` : ''}Chapter {pad(ch.number)}</p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="mt-5 font-celestial text-4xl sm:text-5xl lg:text-6xl font-light text-slate-100 leading-[1.05]" data-testid="chapter-title">{ch.title}</motion.h1>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="chapter-meta">
            <span>{ch.book.author}</span>
            <span>{ch.word_count ? `${ch.reading_minutes} min read · ${ch.word_count.toLocaleString()} words` : 'Text pending'}</span>
            {ch.word_count > 0 && <a href={`${API}/books/${slug}/chapters/${ch.slug}/markdown`} download className="flex items-center gap-2 text-slate-400 hover:text-amber-300" data-testid="chapter-download-md"><Download size={12} /> .md</a>}
          </div>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-5 lg:px-10 pb-28 lg:pb-40">
        <div className="border-t border-white/[0.07] pt-14">
          {ch.body_md ? <PaperBody markdown={ch.body_md} lead /> : (
            <div className="rounded-2xl border border-dashed border-white/[0.1] p-8 text-center" data-testid="chapter-pending">
              <p className="font-mono-precision text-xs tracking-[0.25em] text-slate-500">CHAPTER TEXT PENDING</p>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">The manuscript for this chapter is held in the Vault and will appear here once transcribed.</p>
            </div>
          )}
        </div>
        <div className="mt-16 rounded-2xl border border-white/[0.08] bg-[#04070f] px-6 py-5 flex flex-col gap-4" data-testid="chapter-copyright">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{ch.book.title} · Chapter {pad(ch.number)}</p>
            <p className="font-celestial text-sm sm:text-base tracking-wide text-amber-100/90">© {ch.book.author}, {new Date(ch.updated_at).getFullYear()}. All rights reserved.</p>
          </div>
          <ShareBar title={`${ch.book.title} · ${ch.title}`} author={ch.book.author} path={`/u99/book/${slug}/${chapter}`} />
        </div>
        <div className="mt-10 pt-10 border-t border-white/[0.07] flex flex-col sm:flex-row gap-4">
          {ch.prev ? <NavCard c={ch.prev} bookSlug={slug} dir="prev" /> : <div className="flex-1" />}
          {ch.next ? <NavCard c={ch.next} bookSlug={slug} dir="next" /> : <div className="flex-1" />}
        </div>
      </article>
    </main>
  );
}
